import { HorizontalDivider, VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { _FormFieldDescription } from "@@/shared/form/_form-field-description";
import type { FormId } from "@@/shared/form/form-and-field-id";

import { ForEach } from "@@/shared/for-each";
import { FormErrorMessages } from "@@/shared/form/form2-error-messages";
import { TextBox } from "@@/shared/text";
import { FieldTitle } from "@@/shared/text/field-title";
import { useTheme } from "@emotion/react";
import type { DeepKeysOf, Translatable, WeekdaysOnOff } from "@towni/common";
import { translation, weekdaysMeta } from "@towni/common";
import { useState } from "react";
import "react-day-picker/dist/style.css";
import type { infer as InferZodType, ZodSchema } from "zod";
import { useForm3Components } from "./form3-components";
import type { Form3ErrorMessage } from "./form3.error-message";
import { useForm3Field } from "./form3.use-form-field";
import { useForm3FieldAutoValidation } from "./form3.use-form-field-auto-validation";

type ValueType = WeekdaysOnOff;

type Props<FormData, FieldId extends DeepKeysOf<FormData>> = {
    readonly className?: string;
    readonly fieldId: FieldId;
    readonly formId?: FormId;

    /** When value for day has not been set, default to this, if not set it will default to `true` */
    readonly defaultsTo?: boolean;
    readonly label?: Translatable;
    readonly labelDescription?: Translatable;
    readonly description?: Translatable;
    readonly hideDescriptionAfterInput?: boolean;
};

const createForm3WeekdayPickerComponent = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
>() => {
    const Form3WeekdayPicker = <
        FieldId extends DeepKeysOf<FormData> = DeepKeysOf<FormData>,
    >(
        props: Props<FormData, FieldId>,
    ) => {
        const theme = useTheme();
        const FormComponents = useForm3Components<FormSchema, FormData>();
        const [errors, setErrors] = useState<Form3ErrorMessage[]>([]);
        const hasErrors = errors.length > 0;
        const field = useForm3Field<
            FormSchema,
            InferZodType<FormSchema>,
            FieldId,
            ValueType
        >({
            fieldId: props.fieldId,
            formId: props.formId,
        });

        useForm3FieldAutoValidation({
            fieldId: props.fieldId,
            formId: props.formId,
            onValidationFinished: setErrors,
        });

        return (
            <FlexColumn
                key={`${props.formId}_${props.fieldId}`}
                tag={`form-field_${props.fieldId}`}
                crossAxis="stretch">
                <FieldTitle
                    text={
                        props.label ??
                        translation({
                            sv: "Veckodagar",
                            en: "Weekdays",
                        })
                    }
                    description={props.labelDescription}
                />
                <VerticalDivider XS />
                <FlexRow
                    mainAxis="space-between"
                    css={{
                        flex: 1,
                        padding: 20,
                        borderRadius: 5,
                        border: `1px solid ${theme.colors.textInput.border.asString}`,
                        backgroundColor:
                            theme.colors.textInput.background.asString,
                    }}>
                    <ForEach
                        itemOf={weekdaysMeta}
                        getKey={wd => wd.key}
                        divider={<HorizontalDivider />}>
                        {meta => (
                            <FlexColumn crossAxis="center">
                                <TextBox
                                    text={meta.name.short}
                                    case="CAPITALIZE"
                                />
                                <VerticalDivider XS />
                                <FormComponents.Checkbox
                                    fieldId={
                                        `${props.fieldId}.${meta.key}` as FieldId
                                    }
                                />
                            </FlexColumn>
                        )}
                    </ForEach>
                </FlexRow>
                <_FormFieldDescription
                    hasErrors={hasErrors}
                    isDirty={field.dirty}
                    description={props.description}
                    hideDescriptionAfterInput={
                        !!props.hideDescriptionAfterInput
                    }
                    css={{
                        marginTop: 5,
                    }}
                />
                <FormErrorMessages errors={errors} />
            </FlexColumn>
        );
    };
    return Form3WeekdayPicker;
};

export { createForm3WeekdayPickerComponent };
